import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TextInput } from "flowbite-react";
import { toast } from "react-toastify";
import { HiOutlineTrash, HiOutlinePlus, HiOutlineTag } from "react-icons/hi";
import { ColorInput } from "../../../atoms/ColorInput";
import { CustomButton } from "../../../atoms/CustomButton";
import { EmptyState } from "../../../atoms/EmptyState";
import { ModalShell } from "../../../atoms/ModalShell";
import { CharacterCounter } from "../../../atoms/CharacterCounter";
import useAuth from "../../../../hooks/useAuth";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import { useSaveTodoBadges } from "../../../../hooks/todos";
import {
  TodoBadge,
  TodoBadgeColor,
  MAX_TODO_BADGES,
  MAX_TODO_BADGE_LABEL_LENGTH,
} from "../../../../types/TodoTypes";
import { DEFAULT_TODO_BADGE_COLOR, normalizeBadgeColor } from "./todoBadges";

interface Props {
  show: boolean;
  onClose: () => void;
  boardId: string;
  badges: TodoBadge[];
}

const BadgesConfigModal: React.FC<Props> = ({
  show,
  onClose,
  boardId,
  badges,
}) => {
  const { t } = useTranslation();
  const { user, customer_identification } = useAuth();
  const permissions = usePermissions(user);
  const canWrite = permissions.can(Cap.TASKS_BOARD_CONFIG, Level.WRITE);
  const save = useSaveTodoBadges(
    customer_identification,
    user.entity_identification
  );

  const [draft, setDraft] = useState<TodoBadge[]>([]);

  useEffect(() => {
    if (show)
      setDraft(
        badges.map((b) => ({ ...b, color: normalizeBadgeColor(b.color) }))
      );
  }, [show, badges]);

  const setLabel = (index: number, label: string) =>
    setDraft((d) => d.map((b, i) => (i === index ? { ...b, label } : b)));
  const setColor = (index: number, color: TodoBadgeColor) =>
    setDraft((d) => d.map((b, i) => (i === index ? { ...b, color } : b)));
  const remove = (index: number) =>
    setDraft((d) => d.filter((_, i) => i !== index));
  const add = () => {
    if (draft.length >= MAX_TODO_BADGES) return;
    setDraft((d) => [
      ...d,
      { id: "", label: "", color: DEFAULT_TODO_BADGE_COLOR },
    ]);
  };

  const handleSave = () => {
    if (!canWrite) return;
    const cleaned = draft.map((b) => ({ ...b, label: b.label.trim() }));
    if (cleaned.some((b) => !b.label)) {
      toast.error(t("todos.badges.labelRequired") as string);
      return;
    }
    save.mutate(
      { boardId, badges: cleaned },
      {
        onSuccess: () => {
          toast.success(t("todos.badges.saved") as string);
          onClose();
        },
        onError: (err) => {
          toast.error(
            (err?.response?.data?.message ||
              t("todos.badges.saveError")) as string
          );
        },
      }
    );
  };

  return (
    <ModalShell
      show={show}
      onClose={onClose}
      size="md"
      dismissible
      title={t("todos.badges.title")}
      subtitle={t("todos.badges.help", { max: MAX_TODO_BADGES })}
      footer={
        <>
          <CustomButton
            variant="secondary"
            title={t("common.cancel") as string}
            onClick={onClose}
          />
          <CustomButton
            variant="primary"
            isLoading={save.isLoading}
            disabled={save.isLoading || !canWrite}
            title={t("common.saveShort") as string}
            onClick={handleSave}
          />
        </>
      }
    >
      <div className="divide-y divide-gray-100">
        {draft.length === 0 && (
          <EmptyState
            className="py-6"
            icon={<HiOutlineTag size={24} />}
            title={t("todos.badges.empty")}
          />
        )}
        {draft.map((badge, index) => (
          <div
            key={index}
            className="flex items-start gap-2 py-2.5 first:pt-0 last:pb-0"
          >
            <div className="flex-1 min-w-0">
              <TextInput
                type="text"
                value={badge.label}
                maxLength={MAX_TODO_BADGE_LABEL_LENGTH}
                disabled={!canWrite}
                placeholder={t("todos.badges.labelPlaceholder") as string}
                onChange={(e) => setLabel(index, e.target.value)}
                className="w-full"
              />
              <CharacterCounter
                currentLength={badge.label.length}
                maxLength={MAX_TODO_BADGE_LABEL_LENGTH}
                className="mt-0.5"
              />
            </div>
            <ColorInput
              value={badge.color}
              disabled={!canWrite}
              onChange={(hex) => setColor(index, hex)}
              aria-label={t("todos.badges.color") as string}
            />
            {canWrite && (
              <CustomButton
                variant="none"
                size="icon"
                onClick={() => remove(index)}
                className="shrink-0 text-gray-400 enabled:hover:text-red-600"
                icon={<HiOutlineTrash size={18} />}
                aria-label={t("todos.badges.remove") as string}
              />
            )}
          </div>
        ))}
      </div>

      {canWrite && draft.length < MAX_TODO_BADGES && (
        <CustomButton
          variant="none"
          size="sm"
          onClick={add}
          className="mt-3 inline-flex gap-1.5 px-0 text-blue-600 enabled:hover:text-blue-800"
          icon={<HiOutlinePlus size={16} />}
          title={t("common.add") as string}
        />
      )}
    </ModalShell>
  );
};

export default BadgesConfigModal;
